<main>
  <loading-overlay [isLoading]="isLoading() > 0" />
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'GlAccountRule_Title' | translateWF: 'GL Account Rules' }}</h1>
        <div class="page__title_buttons">
          @if (canEdit) {
            <p-button icon="pi pi-plus" [label]="'GlAccountRule_New' | translateWF: 'New rule'" [disabled]="!companyId()" (onClick)="openNew()" />
          }
        </div>
      </div>
      <p class="text-sm text-[var(--p-text-muted-color)]">
        {{ 'GlAccountRule_Subtitle' | translateWF: 'Which account each line takes by cost center, concept or inventory type. The most specific rule always wins.' }}
        @if (companyName()) {
          <span class="ml-2">· {{ companyName() }}</span>
        }
      </p>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p-selectbutton [options]="viewOptions()" [ngModel]="view()" (ngModelChange)="onViewChange($event)" optionLabel="label" optionValue="value" [allowEmpty]="false" />
      <span class="text-sm text-[var(--p-text-muted-color)]">{{ rules().length }} {{ 'GlAccountRule_ActiveRules' | translateWF: 'active rules' }}</span>
    </div>

    <div class="grid grid-cols-12 gap-[2rem]">
      <div class="col-span-12 md:col-span-4 xl:col-span-3">
        <p-card [header]="sideTitle()" styleClass="rule-side">
          <p-iconfield styleClass="w-full mb-2">
            <p-inputicon styleClass="pi pi-search" />
            <input
              pInputText
              type="search"
              class="w-full"
              [ngModel]="search()"
              (ngModelChange)="onSearch($event)"
              [placeholder]="'GlAccountRule_Search' | translateWF: 'Search by name or code'"
              [attr.aria-label]="'GlAccountRule_Search' | translateWF: 'Search by name or code'"
            />
          </p-iconfield>
          <div class="rule-side__list">
            @for (item of sideItems(); track item.value) {
              <button
                type="button"
                class="rule-side__item"
                [class.rule-side__item--selected]="item.value === selectedValue()"
                [style.padding-left.rem]="0.5 + item.depth * 0.9"
                [attr.aria-pressed]="item.value === selectedValue()"
                (click)="onSelect(item.value)"
              >
                <span class="rule-side__text">
                  <span class="rule-side__title">{{ item.title }}</span>
                  <span class="rule-side__subtitle">{{ item.subtitle }}</span>
                </span>
                @if (item.incomplete) {
                  <p-tag severity="warn" [value]="'GlAccountRule_Incomplete' | translateWF: 'Incomplete'" />
                }
                <p-tag severity="secondary" [value]="'' + item.ruleCount" />
              </button>
            } @empty {
              <p class="text-sm text-center text-[var(--p-text-muted-color)] p-3">{{ 'GlAccountRule_NoMatches' | translateWF: 'Nothing matches.' }}</p>
            }
          </div>
        </p-card>
      </div>

      <div class="col-span-12 md:col-span-8 xl:col-span-9">
        @switch (view()) {
          @case ('costCenter') {
            <div class="flex flex-col gap-4">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div class="text-sm text-[var(--p-text-muted-color)]">{{ costCenterHeader().path }}</div>
                  <h2 class="rule-heading">
                    {{ costCenterHeader().name }} <span class="font-normal text-[var(--p-text-muted-color)]">· {{ costCenterHeader().code }}</span>
                  </h2>
                </div>
                <div class="flex gap-2">
                  <div class="rule-stat">
                    <span class="rule-stat__label">{{ 'GlAccountRule_Stat_Own' | translateWF: 'Own' }}</span>
                    <span class="rule-stat__value">{{ costCenterCounts().own }}</span>
                  </div>
                  <div class="rule-stat">
                    <span class="rule-stat__label">{{ 'GlAccountRule_Stat_Inherited' | translateWF: 'Inherited' }}</span>
                    <span class="rule-stat__value">{{ costCenterCounts().inherited }}</span>
                  </div>
                  <div class="rule-stat">
                    <span class="rule-stat__label">{{ 'GlAccountRule_Stat_Overridden' | translateWF: 'Overridden' }}</span>
                    <span class="rule-stat__value">{{ costCenterCounts().overridden }}</span>
                  </div>
                </div>
              </div>

              @if (costCenterGaps().length > 0) {
                <p-message severity="warn">
                  <div class="flex flex-col gap-1">
                    <strong>{{ 'GlAccountRule_Gap_Title' | translateWF: 'Lines that will not resolve an account in this cost center' }}</strong>
                    @for (gap of costCenterGaps(); track $index) {
                      <span>{{ gap }}</span>
                    }
                  </div>
                </p-message>
              }

              <p-table [value]="costCenterRows()" [rowHover]="true" dataKey="rule.id">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'GlAccountRule_Col_Scope' | translateWF: 'Scope' }}</th>
                    <th>{{ 'GlAccountRule_Col_Dimension' | translateWF: 'Concept / inventory type' }}</th>
                    <th>{{ 'GlAccountRule_Col_Role' | translateWF: 'Role' }}</th>
                    <th>{{ 'GlAccountRule_Col_Account' | translateWF: 'GL account' }}</th>
                    <th>{{ 'GlAccountRule_Col_Origin' | translateWF: 'Origin' }}</th>
                    <th>{{ 'Status' | translateWF: 'Status' }}</th>
                    <th class="text-right" style="width: 4rem">
                      <span class="sr-only">{{ 'Actions' | translateWF: 'Actions' }}</span>
                    </th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td class="text-[var(--p-text-muted-color)]">{{ row.scopeLabel }}</td>
                    <td class="font-medium">{{ row.dimensionLabel }}</td>
                    <td>{{ row.roleLabel }}</td>
                    <td [class.line-through]="row.applies === false">
                      <div class="font-medium tabular-nums">{{ row.rule.glAccount.number }}</div>
                      <div class="text-sm text-[var(--p-text-muted-color)]">{{ accountName(row.rule) }}</div>
                    </td>
                    <td>
                      @if (!row.isOwn) {
                        <i class="pi pi-arrow-up-left mr-1 text-[var(--p-text-muted-color)]" aria-hidden="true"></i>
                      }
                      {{ row.origin }}
                    </td>
                    <td>
                      @if (row.applies === true) {
                        <p-tag severity="success" [value]="'GlAccountRule_Status_Applies' | translateWF: 'Applies'" />
                      } @else if (row.applies === false) {
                        <p-tag severity="secondary" [value]="'GlAccountRule_Status_Overridden' | translateWF: 'Overridden'" />
                      }
                    </td>
                    <td class="text-right">
                      <p-button
                        [icon]="canEdit ? 'pi pi-pencil' : 'pi pi-info-circle'"
                        [text]="true"
                        [rounded]="true"
                        [ariaLabel]="canEdit ? ('GlAccountRule_Edit' | translateWF: 'Edit rule') : ('GlAccountRule_View' | translateWF: 'View rule')"
                        (onClick)="openEdit(row.rule)"
                      />
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="7" class="text-center">
                      {{ 'GlAccountRule_Empty_CostCenter' | translateWF: 'No rule applies to this cost center, neither its own nor inherited.' }}
                    </td>
                  </tr>
                </ng-template>
              </p-table>
              <p class="text-sm text-[var(--p-text-muted-color)]">
                {{
                  'GlAccountRule_Precedence'
                    | translateWF
                      : 'A rule with more dimensions wins; between equals, the cost center nearest the line wins over its parents, and the general rule applies only when nothing more specific does.'
                }}
              </p>
            </div>
          }

          @case ('concept') {
            <div class="flex flex-col gap-4">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div class="text-sm text-[var(--p-text-muted-color)]">
                    {{ 'GlAccountRule_Scope_ExpenseClaim' | translateWF: 'Expense claim' }} · {{ 'GlAccountRule_Details_Form_Field_Concept' | translateWF: 'Concept' }}
                  </div>
                  <h2 class="rule-heading">
                    {{ conceptHeaderName() }} <span class="font-normal text-[var(--p-text-muted-color)]">· {{ selectedConcept() }}</span>
                  </h2>
                </div>
                @if (canEdit && selectedConcept()) {
                  <p-button
                    icon="pi pi-plus"
                    [outlined]="true"
                    [label]="'GlAccountRule_AddException' | translateWF: 'Add exception'"
                    (onClick)="openNewWith(Scope.ExpenseClaim, Role.Expense, null, selectedConcept(), null)"
                  />
                }
              </div>

              <h3 class="rule-subheading">{{ 'GlAccountRule_Ladder' | translateWF: 'Specificity ladder' }}</h3>
              @if (ladderRows().length === 0) {
                <p-message severity="warn">
                  {{ 'GlAccountRule_Ladder_Empty' | translateWF: 'This concept has no rules. No expense claim line with it can post until at least the general rule exists.' }}
                </p-message>
              }
              <div class="flex flex-col gap-2">
                @for (row of ladderRows(); track row.rule.id) {
                  <div class="rule-ladder" [style.margin-left.rem]="row.depth * 1.75">
                    <p-tag [severity]="row.depth === 0 ? 'secondary' : 'info'" [value]="row.step" styleClass="rule-ladder__step" />
                    <div class="flex-1 min-w-0">
                      <div class="font-medium">{{ row.target }}</div>
                      <div class="text-sm text-[var(--p-text-muted-color)] tabular-nums">{{ row.rule.costCenterCode ?? '—' }} · {{ row.roleLabel }}</div>
                    </div>
                    <div class="rule-ladder__account">
                      <div class="font-medium tabular-nums">{{ row.rule.glAccount.number }}</div>
                      <div class="text-sm text-[var(--p-text-muted-color)]">{{ accountName(row.rule) }}</div>
                    </div>
                    <p-button
                      [icon]="canEdit ? 'pi pi-pencil' : 'pi pi-info-circle'"
                      [text]="true"
                      [rounded]="true"
                      [ariaLabel]="canEdit ? ('GlAccountRule_Edit' | translateWF: 'Edit rule') : ('GlAccountRule_View' | translateWF: 'View rule')"
                      (onClick)="openEdit(row.rule)"
                    />
                  </div>
                }
              </div>

              <h3 class="rule-subheading">{{ 'GlAccountRule_Coverage' | translateWF: 'Resulting expense account per cost center' }}</h3>
              <p-table [value]="coverageRows()" [rowHover]="true" dataKey="code" [paginator]="true" [rows]="25" [rowsPerPageOptions]="[25, 50, 100]">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'GlAccountRule_Details_Form_Field_CostCenter' | translateWF: 'Cost center' }}</th>
                    <th>{{ 'GlAccountRule_Col_ResultAccount' | translateWF: 'Resulting account' }}</th>
                    <th>{{ 'GlAccountRule_Col_ResolvedBy' | translateWF: 'Resolved by' }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td [style.padding-left.rem]="0.75 + row.depth * 0.9">
                      <div class="font-medium">{{ row.name }}</div>
                      <div class="text-sm text-[var(--p-text-muted-color)] tabular-nums">{{ row.code }}</div>
                    </td>
                    <td>
                      <div class="font-medium tabular-nums">{{ row.account }}</div>
                      <div class="text-sm text-[var(--p-text-muted-color)]">{{ row.accountName }}</div>
                    </td>
                    <td>
                      @if (row.resolved) {
                        {{ row.via }}
                      } @else {
                        <p-tag severity="warn" [value]="row.via" />
                      }
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </div>
          }

          @default {
            <div class="flex flex-col gap-4">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div class="text-sm text-[var(--p-text-muted-color)]">{{ 'GlAccountRule_Scope_InventoryMovement' | translateWF: 'Inventory movement' }}</div>
                  <h2 class="rule-heading">{{ inventoryHeaderName() }}</h2>
                </div>
              </div>

              @if (inventoryWarningList().length > 0) {
                <p-message severity="warn">
                  <div class="flex flex-col gap-1">
                    <strong>{{ 'GlAccountRule_Warn_Title' | translateWF: 'Incomplete configuration' }}</strong>
                    @for (warning of inventoryWarningList(); track $index) {
                      <span>{{ warning }}</span>
                    }
                  </div>
                </p-message>
              }

              <p-table [value]="inventoryRows()" [rowHover]="true">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 11rem">{{ 'GlAccountRule_Col_AppliesIn' | translateWF: 'Applies in' }}</th>
                    <th>
                      {{ 'GlAccountRule_Role_Inventory' | translateWF: 'Inventory' }}
                      <div class="rule-grid__hint">{{ 'GlAccountRule_Hint_Inventory' | translateWF: 'debit on receipt' }}</div>
                    </th>
                    <th>
                      {{ 'GlAccountRule_Role_Offset' | translateWF: 'Offset' }}
                      <div class="rule-grid__hint">{{ 'GlAccountRule_Hint_Offset' | translateWF: 'credit on receipt' }}</div>
                    </th>
                    <th>
                      {{ 'GlAccountRule_Role_Expense' | translateWF: 'Expense' }}
                      <div class="rule-grid__hint">{{ 'GlAccountRule_Hint_Expense' | translateWF: 'on consumption' }}</div>
                    </th>
                    <th>
                      {{ 'GlAccountRule_Role_Tax' | translateWF: 'Tax' }}
                      <div class="rule-grid__hint">{{ 'GlAccountRule_Hint_Tax' | translateWF: 'optional' }}</div>
                    </th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <th scope="row" class="text-left">
                      <div>{{ row.label }}</div>
                      <div class="text-sm font-normal text-[var(--p-text-muted-color)] tabular-nums">
                        {{ row.costCenterCode ?? ('GlAccountRule_GeneralRules' | translateWF: 'general rules') }}
                      </div>
                    </th>
                    @for (cell of row.cells; track cell.role) {
                      <td>
                        @if (cell.rule; as rule) {
                          <button type="button" class="rule-cell" (click)="openEdit(rule)">
                            <span class="font-medium tabular-nums">{{ rule.glAccount.number }}</span>
                            <span class="text-sm text-[var(--p-text-muted-color)]">{{ accountName(rule) }}</span>
                          </button>
                        } @else if (canEdit) {
                          <button
                            type="button"
                            class="rule-cell rule-cell--empty"
                            (click)="openNewWith(Scope.InventoryMovement, cell.role, row.costCenterCode, null, selectedInventoryType())"
                          >
                            <i class="pi pi-plus" aria-hidden="true"></i>
                            {{ 'GlAccountRule_Add' | translateWF: 'Add' }}
                          </button>
                        } @else {
                          <span class="text-[var(--p-text-muted-color)]">—</span>
                        }
                      </td>
                    }
                  </tr>
                </ng-template>
              </p-table>
              @if (canEdit && selectedInventoryType()) {
                <div>
                  <p-button
                    icon="pi pi-plus"
                    [outlined]="true"
                    [label]="'GlAccountRule_AddCostCenterException' | translateWF: 'Add cost center exception'"
                    (onClick)="openNewWith(Scope.InventoryMovement, Role.Expense, null, null, selectedInventoryType())"
                  />
                </div>
              }
              <p class="text-sm text-[var(--p-text-muted-color)]">
                {{
                  'GlAccountRule_Inventory_Note'
                    | translateWF
                      : 'Each role is its own rule: a receipt takes Inventory + Offset; consumption takes Expense by the receiving cost center. Nothing posts with these rules yet.'
                }}
              </p>
            </div>
          }
        }
      </div>
    </div>

    <p-confirmDialog />
    @if (drawer(); as request) {
      @if (companyId(); as company) {
        <app-gl-account-rule-drawer
          [request]="request"
          [financialCompanyReference]="company"
          [costCenters]="costCenters()"
          [concepts]="concepts()"
          [inventoryTypes]="inventoryTypes()"
          [canEdit]="canEdit"
          (closed)="onDrawerClosed($event)"
        />
      }
    }
  </div>
</main>
